import type { ReactNode } from 'react';

export type RarityBadgeVariant = 'sticker' | 'text' | 'glow';

export interface RarityBadgeProps {
  variant: RarityBadgeVariant;
  children: ReactNode;
  /**
   * Required for variant="glow", ignored otherwise.
   *
   * Must be a hex color string (e.g. `"#22E11C"`). CSS variables are not
   * supported because the implementation concatenates an alpha-hex suffix
   * (`${color}88` for shadow, `${color}66` for border); browsers would
   * silently drop `var(--…)66` as invalid CSS, killing the glow. If
   * omitted, the glow effect collapses (no visible border/shadow).
   */
  glowColor?: string;
  className?: string;
}

const TEXT_BG = 'linear-gradient(180deg, #E99F1F 0%, #FFFFFF 222.22%)';

/**
 * RarityBadge — visual marker для редкости карты / айтема.
 *
 * Variants (выделено из JustPulledCard / JustPulledChip / WinnerStep):
 * - `sticker`  — баннер-стикер с золотым градиентом и белой рамкой
 * - `text`     — inline gradient-text (без фона, без border)
 * - `glow`     — pill с динамическим glow-цветом (теневой ореол); glowColor
 *               задаёт цвет border/shadow; fallback — `var(--ui-accent)`.
 */
export function RarityBadge({
  variant,
  children,
  glowColor,
  className = '',
}: RarityBadgeProps) {
  if (variant === 'sticker') {
    return (
      <span
        data-co-component="RarityBadge"
        className={
          '[border-bottom-left-radius:var(--co-RarityBadge-borderRadius,10px)] [border-bottom-right-radius:var(--co-RarityBadge-borderRadius,10px)] ' +
          'border [border-color:var(--co-RarityBadge-stickerBorder,rgba(255,255,255,0.38))] ' +
          'type-subhead-semi uppercase [color:var(--co-RarityBadge-stickerText,#fff)] ' +
          '[font-family:var(--font-primary,var(--font-sans))] ' +
          'px-[9px] py-[2px] ' +
          '[text-shadow:0_1px_2px_rgba(25,25,25,0.1)] ' +
          className
        }
        style={{
          backgroundImage: `linear-gradient(180deg, var(--co-RarityBadge-stickerGradientFrom, #E99F1F) 0%, var(--co-RarityBadge-stickerGradientTo, #FFFFFF) 222%), linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.1) 100%)`,
        }}
      >
        {children}
      </span>
    );
  }

  if (variant === 'text') {
    return (
      <span
        data-co-component="RarityBadge"
        className={
          'type-subhead-semi shrink-0 whitespace-nowrap uppercase ' +
          'bg-clip-text text-transparent ' +
          className
        }
        style={{ backgroundImage: TEXT_BG }}
      >
        {children}
      </span>
    );
  }

  // variant === 'glow'
  const color = glowColor ?? 'var(--ui-accent)';
  return (
    <span
      data-co-component="RarityBadge"
      className={
        'rounded-full px-4 py-1 type-subhead-semi uppercase tracking-wide ' +
        'bg-white/10 backdrop-blur ' +
        className
      }
      style={{
        color,
        boxShadow: `0 0 18px 0 ${color}88, inset 0 0 8px 0 ${color}44`,
        border: `1px solid ${color}66`,
      }}
    >
      {children}
    </span>
  );
}
